import React from 'react';
import { AmountFormat, ClickToCopy, TagList } from '@monavate/ui-core';
import _ from 'lodash';
import { getIbanType } from '@/helpers/getIbanType';

// only prefund accounts have a threshold, and an unset one shows a dash so the row stays visible
const renderLowBalance = (iban: any) => {
    if (iban.type !== 'prefund') return null;

    const hasLowBalance = iban.lowBalance !== undefined && iban.lowBalance !== null;
    if (!hasLowBalance) return <span data-test-id="value-iban-low-balance">-</span>;

    const lowBalance = iban.lowBalance.toString();
    return (
        <ClickToCopy dataTestId="value-iban-low-balance" value={lowBalance}>
            <AmountFormat amount={lowBalance} currency={iban.currency} />
        </ClickToCopy>
    );
};

export const createIbanDetailsObject = (iban: any) => {
    const ownerName = _.get(iban, 'ownerName', '');
    const ibanNumber = _.get(iban, 'iban', '');
    const currency = _.get(iban, 'currency', '');
    const type = _.get(iban, 'type', '');
    const bic = _.get(iban, 'bankBIC', '');
    const balance = iban.balance.toString();
    const minBalance = iban.minBalance || iban.minBalance === 0 ? iban.minBalance.toString() : '';
    return [
        {
            rows: [
                {
                    name: 'Owner name',
                    customComponentValue: ownerName && (
                        <ClickToCopy dataTestId="value-iban-owner-name" value={ownerName}>
                            {ownerName}
                        </ClickToCopy>
                    ),
                },

                {
                    name: 'Account',
                    customComponentValue: ibanNumber && (
                        <ClickToCopy dataTestId="value-iban-account" value={ibanNumber}>
                            {ibanNumber}
                        </ClickToCopy>
                    ),
                },

                {
                    name: 'Currency',
                    customComponentValue: currency && (
                        <ClickToCopy dataTestId="value-iban-currency" value={currency}>
                            <TagList
                                tagsToShow={1}
                                values={[currency].map((currency) => currency)}
                            />
                        </ClickToCopy>
                    ),
                },

                {
                    name: 'Type',
                    customComponentValue: type && (
                        <ClickToCopy dataTestId="value-iban-type" value={type}>
                            {getIbanType(type)}
                        </ClickToCopy>
                    ),
                },

                {
                    name: 'BIC',
                    customComponentValue: bic && (
                        <ClickToCopy dataTestId="value-iban-bic" value={bic}>
                            {bic}
                        </ClickToCopy>
                    ),
                },

                {
                    name: 'Min. balance',
                    customComponentValue: minBalance && (
                        <ClickToCopy dataTestId="value-iban-min-balance" value={minBalance}>
                            <AmountFormat amount={minBalance} currency={currency} />
                        </ClickToCopy>
                    ),
                },

                {
                    name: 'Low balance',
                    customComponentValue: renderLowBalance(iban),
                },

                {
                    name: 'Balance',
                    customComponentValue: balance && (
                        <ClickToCopy dataTestId="value-iban-balance" value={balance}>
                            <AmountFormat amount={balance} currency={currency} />
                        </ClickToCopy>
                    ),
                },
            ],
        },
    ];
};

export const createIbanAddressObject = (address: any) => {
    const country = _.get(address, 'country', '');
    const city = _.get(address, 'line1', '');
    const clientAddress = _.get(address, 'line2', '');
    return [
        {
            rows: [
                {
                    name: 'Country',
                    customComponentValue: country && (
                        <ClickToCopy dataTestId="value-iban-address-country" value={country}>
                            {country}
                        </ClickToCopy>
                    ),
                },

                {
                    name: 'City',
                    customComponentValue: city && (
                        <ClickToCopy dataTestId="value-iban-address-city" value={city}>
                            {city}
                        </ClickToCopy>
                    ),
                },

                {
                    name: 'Address',
                    customComponentValue: clientAddress && (
                        <ClickToCopy dataTestId="value-iban-address-line" value={clientAddress}>
                            {clientAddress}
                        </ClickToCopy>
                    ),
                },
            ],
        },
    ];
};
